<template>
  <Wrapper class="q-px-lg q-py-xl">
    <h5 class="q-mb-xl">Conversation Management</h5>

    <div class="row">
      <div class="col-12 col-md-9">
        <UserFilter :sort.sync="sort" :search.sync="search" @click:search="debouncedSendRequest" @click:reset="reset" />
      </div>
      <div class="col-12 col-md-3 bg-gray q-pa-md q-mb-md text-right">
        <q-btn class="q-ma-md" outline color="primary" label="Censor" icon="flag" @click="openCensorDialog" />
      </div>
    </div>

    <DataTable ref="table" :columns="columns">
      <template v-if="isLoading">
        <tr v-for="n in 10" :key="n">
          <td v-for="m in columns.length" :key="m">
            <q-skeleton type="text" />
          </td>
        </tr>
      </template>

      <template v-else-if="!conversations.length">
        <tr>
          <td class="text-center" :colspan="columns.length">
            <span v-if="search">No conversations found.</span>
            <span v-else>There are currently no conversation yet.</span>
          </td>
        </tr>
      </template>

      <tr v-for="c in conversations" v-else :key="c.id">
        <td>{{ c.id | formatId }}</td>

        <td>
          <div class="q-mb-xs">
            <span class="text-capitalize">
              {{ c.quoteResponse.request.shipper.name }}
            </span>
            -
            <span class="text-uppercase">
              {{ c.quoteResponse.request.shipper.businessName }}
            </span>
          </div>
          <div class="text-muted">{{ c.quoteResponse.request.shipper.email }}</div>
        </td>

        <td>
          <div class="q-mb-xs">
            <span v-if="c.quoteResponse.company.repDisplayName">
              {{ c.quoteResponse.company.repDisplayName }}
            </span>
            <span v-else class="text-capitalize">
              {{ c.quoteResponse.company.repFirstName }}
            </span>
            -
            <span class="text-uppercase">
              {{ c.quoteResponse.company.tradingName }}
            </span>
          </div>
          <div class="text-muted">{{ c.quoteResponse.transporter.email }}</div>
        </td>

        <td><q-icon v-if="c.flagged" color="negative" name="flag" size="sm" /></td>
        <td>{{ c.updatedAt | formatDateTime }}</td>

        <td>
          <router-link class="q-mb-xs" :to="`conversation/${c.id}`"> View </router-link>
        </td>
      </tr>
    </DataTable>

    <Pagination v-if="totalPages > 1" :page.sync="currentPage" :total="totalPages" />

    <q-dialog v-model="censorDialog" :persistent="true" loading="true" @hide="closeCensorDialog">
      <q-card style="width: 700px; max-width: 80vw">
        <q-card-section>
          <h6>Flagging Words</h6>
        </q-card-section>

        <q-card-section>
          <q-input
            v-model="filter"
            label="Keyword"
            hint="Press enter to create new keyword"
            @keydown.enter.prevent="createFilter"
          />
        </q-card-section>

        <q-card-section style="min-height: 50px">
          <div class="q-gutter-xs">
            <q-chip
              v-for="f in filters"
              :key="f.id"
              removable
              color="mute"
              text-color="black"
              @remove="removeFilter(f.id)"
            >
              {{ f.rule }}
            </q-chip>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn v-close-popup flat color="muted" label="close" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </Wrapper>
</template>

<script>
import _debounce from 'lodash/debounce';
import API from '@/apis';
import Wrapper from '@/components/Wrapper.vue';
import UserFilter from '@/components/UserFilter.vue';
import DataTable from '@/components/DataTableNew.vue';
import Pagination from '@/components/Pagination.vue';
import { getErrorMessage } from '@/utils';
import formatDateMixin from '@/utils/mixins/formatDateMixin';

export default {
  name: 'AdminConversationManagement',

  components: {
    Wrapper,
    UserFilter,
    DataTable,
    Pagination,
  },

  mixins: [formatDateMixin],

  data() {
    return {
      conversations: [],
      totalPages: 1,
      currentPage: 1,
      sort: 'DESC',
      search: '',
      isLoading: true,
      columns: ['ID', 'Shipper', 'Transporter', 'Flagged', 'Last Active Time', 'Action'],
      censorDialog: false,
      filterLoading: false,
      filter: '',
      filters: [],
    };
  },

  watch: {
    currentPage() {
      this.debouncedSendRequest();
      window.scrollTo({ top: this.$refs.table.$el.offsetTop, behavior: 'smooth' });
    },
  },

  created() {
    this.debouncedSendRequest = _debounce(this.sendRequest, 500);
    this.sendRequest();
  },

  methods: {
    getParams() {
      const params = {};

      if (this.search) {
        params.search = this.search;
      }

      if (this.sort !== 'DESC') {
        params.sort = this.sort;
      }

      if (this.currentPage > 1) {
        params.page = this.currentPage;
      }

      return params;
    },
    async sendRequest() {
      this.isLoading = true;
      const params = this.getParams();
      try {
        const {
          data: {
            data,
            pagination: { page, pages },
          },
        } = await this.$axios.request(API.admin.getConversations(params));

        this.conversations = data;
        this.currentPage = page;
        this.totalPages = pages;
      } catch (err) {
        this.$q.notify({
          message: getErrorMessage(err),
          type: 'negative',
          actions: [{ label: 'Try again', color: 'yellow', handler: this.sendRequest }],
        });
      } finally {
        this.isLoading = false;
      }
    },
    debouncedSendRequest() {},
    reset() {
      if (this.search === '' && this.sort === 'DESC') return;

      this.search = '';
      this.sort = 'DESC';
      this.debouncedSendRequest();
    },
    openCensorDialog() {
      this.getFilters();
      this.censorDialog = true;
    },
    closeCensorDialog() {
      this.filter = '';
    },
    async getFilters() {
      this.filterLoading = true;
      try {
        const { data } = await this.$axios.request(API.admin.getFilters());
        this.filters = data;
      } catch (err) {
        this.$q.notify({
          message: getErrorMessage(err),
          type: 'negative',
        });
      } finally {
        this.filterLoading = false;
      }
    },
    removeFilter(id) {
      const self = this;
      this.$q
        .dialog({
          title: 'Confirm',
          message: 'Are you sure?',
          cancel: true,
          persistent: true,
          ok: {
            text: 'delete',
            color: 'negative',
          },
        })
        .onOk(async () => {
          try {
            await this.$axios.request(API.admin.deleteFilter(id));
            this.$q.notify({
              message: 'Filter deleted successfully!',
              type: 'positive',
            });
            await self.getFilters();
            self.debouncedSendRequest();
          } catch (e) {
            this.$q.notify({
              message: getErrorMessage(e),
              type: 'negative',
            });
          }
        });
    },
    async createFilter() {
      const rule = this.filter.trim();

      if (rule.length === 0) {
        return;
      }
      try {
        await this.$axios.request(API.admin.createFilter({ rule }));
        this.$q.notify({
          message: 'Filter added successfully!',
          type: 'positive',
        });
        await this.getFilters();
        this.filter = '';
        this.debouncedSendRequest();
      } catch (e) {
        this.$q.notify({
          message: getErrorMessage(e),
          type: 'negative',
        });
      }
    },
  },
};
</script>
